import { apiClient } from '../utils/api-client';
// Shared so the server's error code survives the throw: the list is refused
// with `dpa_required` until the circuit signs, and the page routes on that.
import { throwForResponse } from './api-error';
import type { EntryGender, RegistrationCategory, RegistrationTeam } from './registrations';

/** One of a person's entries, as the circuit-level view lists them. */
export interface ParticipantStage {
  entry_id: string;
  stage_id: string;
  stage_name: string;
  stage_slug: string;
  /** Null at a stage that takes no fee. */
  paid: boolean | null;
}

/**
 * One person taking part in the circuit, gathered from their entries across its
 * stages. `id` is one of those entries — the one their details are read from,
 * and the handle everything else uses.
 */
export interface TrophyParticipant {
  id: string;
  name?: string | null;
  surname?: string | null;
  full_name?: string | null;
  email?: string | null;
  birth_date?: string | null;
  /** The gender of their class; null where nobody gave it. */
  gender?: EntryGender | null;
  /** Under 14 by the date of birth: the letter goes to the guardian, not to them. */
  minor?: boolean;
  /** The parent's or guardian's address, on an entrant under 14. */
  guardian_email?: string | null;
  /** The guardian has opened the child's managed account. */
  managed?: boolean;
  category_id?: string | null;
  category_name?: string | null;
  team_id?: string | null;
  team_name?: string | null;
  user_slug?: string | null;
  user_username?: string | null;
  /** Told they were entered, but not signed up yet. */
  invited?: boolean;
  stages: ParticipantStage[];
  stages_count: number;
  paid_count: number;
}

export interface TrophyParticipantPage {
  participants: TrophyParticipant[];
  total: number;
  categories: RegistrationCategory[];
  teams: RegistrationTeam[];
  stages: { id: string; name: string; slug: string }[];
  /** Whether the form asks the gender: the circuit's agreement covers it. */
  genderCollected: boolean;
}

/** What the circuit-level form edits. No payment: money is a stage's business. */
export interface TrophyParticipantPayload {
  name?: string | null;
  surname?: string | null;
  email?: string | null;
  guardian_email?: string | null;
  birth_date?: string | null;
  competition_category_id?: string | null;
  competition_team_id?: string | null;
  /** Sent only where the circuit's agreement covers it; blank clears it. */
  gender?: EntryGender | '' | null;
}

interface ParticipantResponse {
  data: { id: string; type: 'trophy_participant'; attributes: TrophyParticipant };
}

interface ParticipantsListResponse {
  data: { id: string; type: 'trophy_participant'; attributes: TrophyParticipant }[];
  meta?: {
    total?: number;
    categories?: RegistrationCategory[];
    teams?: RegistrationTeam[];
    stages?: { id: string; name: string; slug: string }[];
    gender_collected?: boolean;
  };
}

/**
 * Everybody taking part in a circuit, one row per person.
 *
 * Not a roster — a trophy keeps none. This is the circuit's view of the entries
 * its stages hold, which is why saving here corrects the person on **every**
 * entry they have: that is the whole reason a circuit-level view exists.
 */
class TrophyParticipantsService {
  async list(trophySlug: string): Promise<TrophyParticipantPage> {
    const response = await apiClient.get(`/api/trophies/${trophySlug}/participants`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch the participants');
    }

    const data: ParticipantsListResponse = await response.json();
    return {
      participants: data.data.map((item) => item.attributes),
      total: data.meta?.total ?? data.data.length,
      categories: data.meta?.categories ?? [],
      teams: data.meta?.teams ?? [],
      stages: data.meta?.stages ?? [],
      genderCollected: data.meta?.gender_collected ?? false,
    };
  }

  /** Corrects this person everywhere they appear in the circuit. */
  async update(
    trophySlug: string,
    id: string,
    attributes: TrophyParticipantPayload
  ): Promise<TrophyParticipant> {
    const response = await apiClient.patch(`/api/trophies/${trophySlug}/participants/${id}`, {
      data: { attributes },
    });

    if (!response.ok) {
      await throwForResponse(response, 'Failed to update the participant');
    }

    const data: ParticipantResponse = await response.json();
    return data.data.attributes;
  }

  /** Takes them out of the whole circuit — every stage they were entered in. */
  async remove(trophySlug: string, id: string): Promise<void> {
    const response = await apiClient.delete(`/api/trophies/${trophySlug}/participants/${id}`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to remove the participant');
    }
  }
}

export const trophyParticipantsService = new TrophyParticipantsService();
